import { useCallback, useEffect, useState } from 'react';
import { fetchActiveProjectBaselineForCurrentUser } from '../logic/projectBaselineGuard';
import { subscribeRequestTrackingInvalidation } from '../logic/requestTrackingInvalidation';
import { runIfDocumentVisible } from '../logic/requestTrackingPoll';
import type { RequestTrackingItem } from '../logic/types';

export interface UseActiveProjectBaselineResult {
  /** True when the current user has a queued/running project baseline request. */
  baselineInProgress: boolean;
  activeRequest: RequestTrackingItem | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

/**
 * Loads active project-baseline request status.
 * Polls only while a baseline request is queued/running (not continuously idle).
 */
export function useActiveProjectBaseline(
  projectId?: string | null,
  pollIntervalMs = 10000,
): UseActiveProjectBaselineResult {
  const [baselineInProgress, setBaselineInProgress] = useState(false);
  const [activeRequest, setActiveRequest] = useState<RequestTrackingItem | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const result = await fetchActiveProjectBaselineForCurrentUser({ projectId });
      setBaselineInProgress(result.blocked);
      setActiveRequest(result.activeRequest);
    } catch {
      setBaselineInProgress(false);
      setActiveRequest(null);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    setLoading(true);
    void refresh();
  }, [refresh]);

  useEffect(() => {
    return subscribeRequestTrackingInvalidation(() => {
      void refresh();
    });
  }, [refresh]);

  useEffect(() => {
    if (pollIntervalMs <= 0 || !baselineInProgress) {
      return;
    }
    const timer = window.setInterval(() => {
      runIfDocumentVisible(() => {
        void refresh();
      });
    }, pollIntervalMs);
    return () => window.clearInterval(timer);
  }, [pollIntervalMs, refresh, baselineInProgress]);

  return { baselineInProgress, activeRequest, loading, refresh };
}
